import { Component, Input, Output, EventEmitter, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { cn } from '../../../utils/class-utils';

interface CalendarDay {
    date: Date;
    isCurrentMonth: boolean;
    isSelected: boolean;
    isToday: boolean;
    isDisabled: boolean;
}

@Component({
    selector: 'jjames-calendar',
    standalone: true,
    imports: [CommonModule],
    template: `
    <div class="bg-white rounded-lg border border-gray-200 shadow-sm p-4">
      <!-- Header with navigation -->
      <div class="flex items-center justify-between mb-4">
        <button
          type="button"
          class="p-2 hover:bg-gray-100 rounded-md transition-colors"
          (click)="previousMonth()"
        >
          <svg class="h-5 w-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        
        <h2 class="text-lg font-semibold text-gray-900">
          {{ getMonthYearLabel() }}
        </h2>
        
        <button
          type="button"
          class="p-2 hover:bg-gray-100 rounded-md transition-colors"
          (click)="nextMonth()"
        >
          <svg class="h-5 w-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>
      
      <!-- Days of week header -->
      <div class="grid grid-cols-7 gap-1 mb-2">
        @for (day of daysOfWeek; track day) {
          <div class="text-center text-xs font-medium text-gray-500 py-2">
            {{ day }}
          </div>
        }
      </div>
      
      <!-- Calendar days -->
      <div class="grid grid-cols-7 gap-1">
        @for (day of calendarDays; track trackByDate($index, day)) {
          <button
            type="button"
            [class]="getDayClasses(day)"
            [disabled]="day.isDisabled"
            (click)="selectDate(day)"
          >
            {{ day.date.getDate() }}
          </button>
        }
      </div>
      
      <!-- Today button -->
      @if (showTodayButton) {
        <div class="mt-4 text-center">
          <button
            type="button"
            class="px-4 py-2 text-sm font-medium text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
            (click)="goToToday()"
          >
            Today
          </button>
        </div>
      }
    </div>
  `,
    styles: []
})
export class JJamesCalendarComponent implements OnInit {
    @Input() selectedDate?: Date;
    @Input() minDate?: Date;
    @Input() maxDate?: Date;
    @Input() disabledDates: Date[] = [];
    @Input() showTodayButton = true;

    @Output() dateSelected = new EventEmitter<Date>();
    @Output() monthChanged = new EventEmitter<Date>();

    currentDate = new Date();
    calendarDays: CalendarDay[] = [];
    daysOfWeek = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

    ngOnInit(): void {
        if (this.selectedDate) {
            this.currentDate = new Date(this.selectedDate);
        }
        this.generateCalendarDays();
    }

    previousMonth(): void {
        this.currentDate.setMonth(this.currentDate.getMonth() - 1);
        this.currentDate = new Date(this.currentDate);
        this.generateCalendarDays();
        this.monthChanged.emit(new Date(this.currentDate));
    }

    nextMonth(): void {
        this.currentDate.setMonth(this.currentDate.getMonth() + 1);
        this.currentDate = new Date(this.currentDate);
        this.generateCalendarDays();
        this.monthChanged.emit(new Date(this.currentDate));
    }

    goToToday(): void {
        this.currentDate = new Date();
        this.generateCalendarDays();
        this.monthChanged.emit(new Date(this.currentDate));
    }

    selectDate(day: CalendarDay): void {
        if (!day.isDisabled) {
            this.selectedDate = new Date(day.date);
            this.generateCalendarDays();
            this.dateSelected.emit(new Date(day.date));
        }
    }

    getMonthYearLabel(): string {
        return this.currentDate.toLocaleDateString('en-US', {
            month: 'long',
            year: 'numeric'
        });
    }

    getDayClasses(day: CalendarDay): string {
        const baseClasses = 'relative p-2 text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 rounded-md';

        let classes = baseClasses;

        if (!day.isCurrentMonth) {
            classes += ' text-gray-400';
        } else if (day.isDisabled) {
            classes += ' text-gray-300 cursor-not-allowed';
        } else {
            classes += ' text-gray-900 hover:bg-gray-100';
        }

        if (day.isSelected) {
            classes += ' bg-blue-600 text-white hover:bg-blue-700';
        } else if (day.isToday && !day.isSelected) {
            classes += ' bg-blue-100 text-blue-600';
        }

        return classes;
    }

    private generateCalendarDays(): void {
        this.calendarDays = [];

        const year = this.currentDate.getFullYear();
        const month = this.currentDate.getMonth();

        // First day of the month
        const firstDay = new Date(year, month, 1);
        const lastDay = new Date(year, month + 1, 0);

        // Start from the first day of the week that contains the first day of the month
        const startDate = new Date(firstDay);
        startDate.setDate(firstDay.getDate() - firstDay.getDay());

        // Generate 42 days (6 weeks)
        for (let i = 0; i < 42; i++) {
            const date = new Date(startDate);
            date.setDate(startDate.getDate() + i);

            const isCurrentMonth = date.getMonth() === month;
            const isToday = this.isSameDay(date, new Date());
            const isSelected = this.selectedDate ? this.isSameDay(date, this.selectedDate) : false;
            const isDisabled = this.isDateDisabled(date);

            this.calendarDays.push({
                date,
                isCurrentMonth,
                isSelected,
                isToday,
                isDisabled
            });
        }
    }

    private isSameDay(date1: Date, date2: Date): boolean {
        return date1.getFullYear() === date2.getFullYear() &&
            date1.getMonth() === date2.getMonth() &&
            date1.getDate() === date2.getDate();
    }

    private isDateDisabled(date: Date): boolean {
        // Check min/max dates
        if (this.minDate && date < this.minDate) {
            return true;
        }
        if (this.maxDate && date > this.maxDate) {
            return true;
        }

        // Check disabled dates
        return this.disabledDates.some(disabledDate => this.isSameDay(date, disabledDate));
    }

    trackByDate(index: number, day: CalendarDay): string {
        return day.date.toISOString();
    }
}